import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Icon } from '@iconify/react';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { useState } from 'react';

import { useResponsive } from 'src/hooks/use-responsive';
import { E_TOOLS_HEADER, E_TOOLS_NAV } from './config';

export default function EToolsHeader({ onOpenNav }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lgUp = useResponsive('up', 'lg');
  const [cookies, , removeCookie] = useCookies(['user', 'access', 'refresh']);
  const user = cookies?.user;

  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);

  const handleLogout = () => {
    removeCookie('access', { path: '/' });
    removeCookie('refresh', { path: '/' });
    removeCookie('user', { path: '/' });
    navigate('/login', { replace: true });
  };

  const handleChangeLang = (lng) => {
    i18n.changeLanguage(lng);
    setLangAnchor(null);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        height: E_TOOLS_HEADER.H_MOBILE,
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        ...(lgUp && {
          width: `calc(100% - ${E_TOOLS_NAV.WIDTH}px)`,
          ml: `${E_TOOLS_NAV.WIDTH}px`,
          height: E_TOOLS_HEADER.H_DESKTOP,
        }),
      }}
    >
      <Toolbar sx={{ height: 1, px: { lg: 4 } }}>
        {!lgUp && (
          <IconButton onClick={onOpenNav} sx={{ mr: 1 }}>
            <Icon icon="eva:menu-2-fill" width={22} />
          </IconButton>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={(e) => setLangAnchor(e.currentTarget)}
            sx={{ color: 'text.primary', textTransform: 'uppercase', fontWeight: 600 }}
            startIcon={<Icon icon="solar:global-bold-duotone" width={18} />}
          >
            {i18n.language}
          </Button>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
          >
            <MenuItem onClick={() => handleChangeLang('uz')}>UZ</MenuItem>
            <MenuItem onClick={() => handleChangeLang('ru')}>RU</MenuItem>
          </Menu>

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {(user?.username?.[0] || 'U').toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { minWidth: 220, mt: 1 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {user?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{ color: 'error.main', fontWeight: 600, mt: 0.5 }}
            >
              <Icon icon="solar:logout-2-bold-duotone" width={18} style={{ marginRight: 8 }} />
              {t('common.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

EToolsHeader.propTypes = {
  onOpenNav: PropTypes.func,
};
